import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import { ErrorIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import { formatDate } from '../../../utils/dateUtils';
import AdminPageShell from '../../components/AdminPageShell';
import AdminListToolbar from '../../components/AdminListToolbar';
import ListPager from '../../components/ListPager';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';

/** Row from GET /admin/committees (Campaign Committee Registry PRD §5.1). */
interface CommitteeRow {
  co_id: string;
  name: string | null;
  committee_type: string | null;
  first_seen: string | null;
  last_seen: string | null;
  has_purpose: boolean;
  measure_count: number;
  candidate_count: number;
  former_name_match: string | null;
}

const parseCommittees = (raw: unknown): AdminListResult<CommitteeRow> => {
  const data = raw as { data?: CommitteeRow[]; pagination?: { total?: number; limit?: number; offset?: number } };
  const total = data.pagination?.total ?? 0;
  const limit = data.pagination?.limit ?? 0;
  const offset = data.pagination?.offset ?? 0;
  return { rows: data.data ?? [], total, hasMore: offset + limit < total };
};

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

const COLUMN_COUNT = 6;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** "2 measures · 1 candidate", or "—" when nothing links to the committee. */
export const linkedSummary = (row: Pick<CommitteeRow, 'measure_count' | 'candidate_count'>) =>
  [
    row.measure_count > 0 ? plural(row.measure_count, 'measure') : null,
    row.candidate_count > 0 ? plural(row.candidate_count, 'candidate') : null,
  ].filter(Boolean).join(' · ') || '—';

/**
 * Committees — every campaign committee in the TRACER campaign finance import.
 * Fields from the import are read-only; staff fill in what it doesn't carry (the
 * purpose statement above all), once, and every measure and candidate linked to
 * the committee shows it (Documentation/CAMPAIGN_COMMITTEE_REGISTRY_PRD.md).
 */
const CommitteesPage: React.FC = () => {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [purpose, setPurpose] = useState('');
  const [linked, setLinked] = useState('');
  const [active, setActive] = useState('true');

  const types = useQuery({
    queryKey: ['admin-committees', 'types'],
    queryFn: async () => {
      const response = await api.get('/admin/committees/types');
      return (response.data.data ?? []) as Array<{ value: string; count: number }>;
    },
    staleTime: 10 * 60 * 1000,
  });

  const list = useAdminList<CommitteeRow>({
    queryKey: 'committees-registry',
    endpoint: '/admin/committees',
    parse: parseCommittees,
    limit: 50,
    filters: {
      type: type || undefined,
      purpose: purpose || undefined,
      linked: linked || undefined,
      active,
    },
    initialSort: { key: 'name', direction: 'asc' },
  });

  return (
    <AdminPageShell
      title="Committees"
      description="Every campaign committee in the TRACER campaign finance import, with the details the import doesn't carry"
      actions={<HelpTrigger contentKey="admin-committees" size="md" ariaLabel="Help for committees" />}
    >
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search by name, former name, or TRACER ID…"
        searchAriaLabel="Search committees"
      >
        <select
          value={type}
          onChange={(event) => setType(event.target.value)}
          aria-label="Filter by committee type"
          className={filterSelectClasses}
        >
          <option value="">All types</option>
          {(types.data ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.value}
            </option>
          ))}
        </select>
        <select
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
          aria-label="Filter by purpose statement"
          className={filterSelectClasses}
        >
          <option value="">Any purpose</option>
          <option value="has">Has purpose</option>
          <option value="missing">Missing purpose</option>
        </select>
        <select
          value={linked}
          onChange={(event) => setLinked(event.target.value)}
          aria-label="Filter by what links to the committee"
          className={filterSelectClasses}
        >
          <option value="">Linked to anything</option>
          <option value="measure">Linked to a ballot measure</option>
          <option value="candidate">Linked to a candidate</option>
          <option value="none">Not linked</option>
        </select>
        <select
          value={active}
          onChange={(event) => setActive(event.target.value)}
          aria-label="Filter by recent filing"
          className={filterSelectClasses}
        >
          <option value="true">Filed in the last 24 months</option>
          <option value="false">All committees</option>
        </select>
      </AdminListToolbar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <ErrorIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load committees. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">TRACER ID</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Type</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="last_seen" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Last filing
              </StandardTableHeaderCell>
              <StandardTableHeaderCell>Purpose</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Linked to</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message="No committees match. Committees appear here after their first TRACER filing is imported."
                icon={<Wallet className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((row) => (
                <StandardTableRow key={row.co_id} onClick={() => navigate(`/admin-next/committees/${encodeURIComponent(row.co_id)}`)}>
                  <StandardTableCell>
                    <Link
                      to={`/admin-next/committees/${encodeURIComponent(row.co_id)}`}
                      onClick={(event) => event.stopPropagation()}
                      className="font-medium text-text-primary hover:underline dark:text-text-dark-primary"
                    >
                      {row.name || row.co_id}
                    </Link>
                    {row.former_name_match && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        formerly {row.former_name_match}
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">
                    <span className="tabular-nums">{row.co_id}</span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{row.committee_type || '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{formatDate(row.last_seen)}</StandardTableCell>
                  <StandardTableCell>
                    {row.has_purpose ? (
                      <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Filled</span>
                    ) : (
                      <span className="text-sm text-warning-700 dark:text-warning-400">Missing</span>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{linkedSummary(row)}</StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="committees"
        />
      )}
    </AdminPageShell>
  );
};

export default CommitteesPage;
